import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BaseFieldComponent } from '../base-field.component';

/**
 * Campo de tipo "paragraph" — solo texto, sin form control activo.
 * El contenido y el estilo se leen de field.customProps:
 *   text      → string (texto a mostrar; soporta [texto](url) para enlaces)
 *   align     → 'left' | 'center' | 'right' | 'justify' (default 'left')
 *   bold      → boolean (default false)
 *   italic    → boolean (default false)
 *   color     → string hex (default '#374151')
 *   fontSize  → number px (default 14)
 */
@Component({
  selector: 'app-paragraph-field',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="af-content-block af-content-block--paragraph" [ngStyle]="wrapperStyles">
      <span [ngStyle]="textStyles" [innerHTML]="safeHtml"></span>
    </div>
  `,
  styles: [`
    .af-content-block {
      width: 100%;
      padding: 2px 0;
      box-sizing: border-box;
    }
    .af-content-block span {
      display: block;
      line-height: 1.6;
      word-break: break-word;
      white-space: pre-wrap;
    }
    .af-content-block span ::ng-deep a {
      color: var(--af-color-border-focus, #6366f1);
      text-decoration: underline;
      cursor: pointer;
    }
    .af-content-block span ::ng-deep a:hover {
      opacity: 0.8;
    }
  `]
})
export class ParagraphFieldComponent extends BaseFieldComponent {

  private readonly sanitizer = inject(DomSanitizer);

  get p(): Record<string, unknown> {
    return (this.field.customProps as Record<string, unknown>) ?? {};
  }

  get displayText(): string {
    return String(this.p['text'] ?? this.field.label ?? 'Párrafo de ejemplo');
  }

  /** Convierte la sintaxis [texto](url) en etiquetas <a> seguras */
  get safeHtml(): SafeHtml {
    const raw = this.escapeHtml(this.displayText);
    const withLinks = raw.replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    return this.sanitizer.bypassSecurityTrustHtml(withLinks);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  get wrapperStyles(): Record<string, string> {
    return { textAlign: String(this.p['align'] ?? 'left') };
  }

  get textStyles(): Record<string, string> {
    return {
      fontSize:   `${this.p['fontSize'] ?? 14}px`,
      fontWeight: this.p['bold'] ? '700' : '400',
      fontStyle:  this.p['italic'] ? 'italic' : 'normal',
      fontFamily: 'var(--af-paragraph-font-family, inherit)',
      color:      String(this.p['color'] ?? '#374151'),
      display:    'block',
    };
  }
}
